import { CompositeType } from '@ee/common/models';

export interface CompositePart {
  key: string;
  label: string;
  /** Derived parts are read-only wherever they appear, with their provenance stated. */
  derived?: boolean;
  /** Never required, even when the composite is: a house has no unit. */
  optional?: boolean;
  /**
   * Carried in the form and submitted, but never asked, drawn or required: the block fills it from a
   * choice or a lookup (the county object behind the county name, the courts, the coordinates, the
   * precinct and its provenance). v1 submits these beside the address and the inbox reads them.
   */
  carried?: boolean;
}

/**
 * A composite is one question with several parts. Its parts are never individually placeable, which is
 * exactly what protects the Mapbox suggestions and the ZIP→county derivation from being broken by a drag.
 *
 * Bindings stay per part, so documents, the sidebar, imports and the API see what they see today.
 */
export const COMPOSITE_PARTS: Record<CompositeType, CompositePart[]> = {
  /*
   * The keys are the wizard's, checked against `eviction-property-section.component.ts` rather than
   * guessed: `property_unit_type` + `property_unit_number` (not one `property_unit`), and
   * `property_zip_code` (not `property_zip`). Written from memory the first time, they matched no form
   * control, so nothing this composite named could ever have resolved.
   *
   * City, state and ZIP are ordinary controls that the autocomplete happens to fill — a client can type
   * over any of them.
   *
   * **The county is not derived either**, which the first pass got wrong. The ZIP narrows it, but a ZIP
   * can straddle a county boundary and only the client knows which side the unit is on — choosing one
   * for them and hiding the choice is how a filing lands in the wrong court. So it is a select whose
   * candidates come from the ZIP, answered automatically only when there is exactly one.
   */
  ADDRESS: [
    { key: 'property_street_address', label: 'Street address' },
    { key: 'property_unit_type', label: 'Unit type', optional: true },
    { key: 'property_unit_number', label: 'Unit number', optional: true },
    { key: 'property_city', label: 'City' },
    { key: 'property_state', label: 'State' },
    { key: 'property_zip_code', label: 'ZIP' },
    // Required by `options.property_county`, applied by the block while the field is on screen, as v1 did.
    { key: 'property_county', label: 'County', optional: true },
    /*
     * What v1's property step submits beside the typed address (`eviction-property-section.component.ts`,
     * `GenerateEvictionPropertyForm`). The server reads `county` (the object: fee settings, courts,
     * processing) and never `property_county`, so without these a v2 submission arrived with no county,
     * no courts, no coordinates and no precinct.
     */
    // Named as the client sees it: the preflight says "County is missing" when the option requires one.
    { key: 'county', label: 'County', carried: true },
    { key: 'property_lat', label: 'Latitude', carried: true },
    { key: 'property_lng', label: 'Longitude', carried: true },
    { key: 'containing_subdivision', label: 'Precinct / township', carried: true },
    { key: 'containing_subdivision_source', label: 'Precinct source', carried: true },
    { key: 'containing_subdivision_kind', label: 'Precinct kind', carried: true },
    { key: 'place', label: 'Place', carried: true },
    { key: 'courts', label: 'Court(s)', carried: true }
  ],
  TENANT_DETAILS: [
    { key: 'first_name', label: 'First name' },
    { key: 'last_name', label: 'Last name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'dob', label: 'Date of birth' },
    { key: 'ssn', label: 'SSN' }
  ],
  SCRA_CHECK: [
    { key: 'scra_status', label: 'Status', derived: true },
    { key: 'scra_checked_at', label: 'Checked', derived: true },
    { key: 'scra_active_duty_start', label: 'Active duty from', derived: true },
    { key: 'scra_active_duty_end', label: 'Active duty to', derived: true }
  ],
  PACER_DETAILS: [
    { key: 'pacer_status', label: 'Status', derived: true },
    { key: 'pacer_checked_at', label: 'Checked', derived: true },
    { key: 'pacer_active_case_count', label: 'Open cases', derived: true },
    { key: 'pacer_most_recent_filing', label: 'Most recent filing', derived: true }
  ]
};

/** The parts a composite asks or shows: everything but the carried ones. */
export function askedParts(type: CompositeType | null | undefined): CompositePart[] {
  return (type ? (COMPOSITE_PARTS[type] ?? []) : []).filter(part => !part.carried);
}

export const COMPOSITE_LABELS: Record<CompositeType, string> = {
  ADDRESS: 'Property address',
  TENANT_DETAILS: 'Tenant details',
  SCRA_CHECK: 'SCRA check',
  PACER_DETAILS: 'PACER details'
};

/**
 * Every part key of every composite, so no surface can offer one on its own.
 *
 * A composite exists because its parts only work together — the address autocomplete writes five of
 * them from one gesture, and the county is resolved from the ZIP. Placing a part by itself gives a firm
 * a control the wizard will overwrite, which is why the picker and the registry both consult this.
 */
export const COMPOSITE_PART_KEYS: ReadonlySet<string> = new Set(
  Object.values(COMPOSITE_PARTS).flatMap(parts => parts.map(part => part.key))
);

/**
 * The unit-type choices the wizard offers when a firm has not narrowed them.
 *
 * The list itself is `UNIT_TYPES`; what a firm may edit is which of them its clients see, stored on the
 * form as `options.property_unit_types.options`. Empty or absent means all of them, which is what
 * `eviction-property-section.component.ts` falls back to.
 */
export const UNIT_TYPE_OPTION_KEY = 'property_unit_types';

/** Result-bearing composites show never-run / running / result plus a re-check, and degrade to read-only text. */
export const RESULT_BEARING: CompositeType[] = ['SCRA_CHECK', 'PACER_DETAILS'];

/**
 * What has to be answered before a check can run. Stated in place — a re-check button that fails with a
 * server error is how a firm concludes the integration is broken when it is only missing a name.
 */
export const CHECK_PRECONDITIONS: Partial<Record<CompositeType, string>> = {
  SCRA_CHECK: 'Add a tenant first name, last name and either a date of birth or an SSN.',
  PACER_DETAILS: 'Add a tenant SSN.'
};
